/**
 * Charging Profiles tab.
 *
 * Three sections:
 *   1. Install template — four buttons, each appends a fully-formed snapshot.
 *   2. Installed profiles — table with purpose-coloured badges + Delete.
 *   3. Active resolution per connector — re-renders every second so the
 *      tester sees the resolver pick a new period as time advances.
 *
 * Composite schedule preview is intentionally deferred to a follow-up patch
 * because the resolver's `findActivePeriod` already shows the active limit
 * live; a static composite chart adds limited value while ChargingProfileResolver
 * is the source of truth.
 */
import { useEffect, useMemo, useState } from 'react';
import { resolveActiveProfile, type ChargingProfileSnapshot, type ResolvedProfile } from '@/state/types';
import type { ChargingProfileId, ConnectorId, TransactionId } from '@/state/types';
import { useStationStore } from '@/state/useStationStore';
import { useChargingProfilesStore } from '@/state/useChargingProfilesStore';
import { TEMPLATES, type TemplateKind } from '@/state/chargingProfileTemplates';
import { useInterval } from '@/hooks/useInterval';
import { useConfirm } from '@/hooks/useConfirm';
import { toast } from '@/state/useToastStore';
import { ChargingProfileTable } from '@/ui/components/ChargingProfileTable';
import { formatTime } from '@/lib/format';

const RESOLUTION_TICK_MS = 1_000;

export function ChargingProfilesTab(): JSX.Element {
  const stationId = useStationStore((s) => s.stationId);
  const connectors = useStationStore((s) => s.connectors);
  const entries = useChargingProfilesStore((s) => s.entries);
  const refresh = useChargingProfilesStore((s) => s.refresh);
  const add = useChargingProfilesStore((s) => s.add);
  const remove = useChargingProfilesStore((s) => s.remove);
  const findNextId = useChargingProfilesStore((s) => s.findNextId);

  // Re-render tick — gives ChargingProfileResolver a fresh `nowMs`.
  const [tick, setTick] = useState<number>(() => Date.now());
  useInterval(() => setTick(Date.now()), RESOLUTION_TICK_MS);

  useEffect(() => {
    void refresh(stationId);
  }, [refresh, stationId]);

  const handleInstall = (kind: TemplateKind): void => {
    const nowMs = Date.now();
    // Pick an active tx id (if any) so the Relative TxProfile template can
    // bind to it; otherwise we synthesise one — the template still installs,
    // just won't resolve until a real tx with that id is running.
    const activeTx = [...connectors.values()].find((c) => c.activeTransactionId !== null);
    const fallbackTxId = 0;
    const transactionId =
      activeTx?.activeTransactionId !== undefined && activeTx?.activeTransactionId !== null
        ? Number(activeTx.activeTransactionId)
        : fallbackTxId;
    try {
      const snap = TEMPLATES[kind].build({
        id: findNextId(),
        nowMs,
        ...(kind === 'relativeTxProfile' ? { transactionId } : {}),
      });
      void add(stationId, snap).then(() => toast(`Profile #${String(snap.id)} installed`));
    } catch {
      // Relative template throws if no transactionId — let the UI just no-op.
    }
  };

  const confirm = useConfirm();

  return (
    <div
      role="tabpanel"
      id="tab-panel-profiles"
      aria-labelledby="tab-profiles"
      className="space-y-4 p-4"
      data-testid="charging-profiles-tab"
    >
      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <h2 className="mb-2 text-sm font-medium text-slate-100">Install template</h2>
        <div className="flex flex-wrap gap-2">
          {(Object.entries(TEMPLATES) as [TemplateKind, (typeof TEMPLATES)[TemplateKind]][]).map(
            ([kind, t]) => (
              <button
                key={kind}
                type="button"
                onClick={() => handleInstall(kind)}
                className="rounded bg-primary-base px-3 py-1 text-xs font-medium text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base"
                data-testid={`template-${kind}`}
              >
                {t.label}
              </button>
            ),
          )}
        </div>
      </section>

      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <h2 className="mb-2 text-sm font-medium text-slate-100">Installed profiles</h2>
        <ChargingProfileTable
          entries={entries}
          onDelete={(id) => {
            confirm(`delete charging profile #${String(id)}`, () => {
              void remove(stationId, id as unknown as ChargingProfileId);
            });
          }}
        />
      </section>

      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <header className="mb-2 flex items-baseline justify-between">
          <h2 className="text-sm font-medium text-slate-100">Active resolution</h2>
          <span className="text-[10px] text-slate-500" data-testid="resolution-tick">
            t={formatTime(tick)}
          </span>
        </header>
        <ResolutionTable connectors={connectors} profiles={entries} nowMs={tick} />
      </section>
    </div>
  );
}

interface ConnectorLike {
  readonly id: ConnectorId;
  readonly activeTransactionId: TransactionId | null;
}

function ResolutionTable({
  connectors,
  profiles,
  nowMs,
}: {
  connectors: ReadonlyMap<ConnectorId, ConnectorLike>;
  profiles: readonly ChargingProfileSnapshot[];
  nowMs: number;
}): JSX.Element {
  const rows = useMemo(() => {
    return [...connectors.values()].map((c) => {
      const txIdNum = c.activeTransactionId !== null ? Number(c.activeTransactionId) : null;
      const resolved: ResolvedProfile | null = resolveActiveProfile({
        profiles,
        connectorId: Number(c.id),
        txId: txIdNum,
        nowMs,
        txStartedAtMs: txIdNum !== null ? nowMs : null,
      });
      return { connectorId: c.id, resolved };
    });
  }, [connectors, profiles, nowMs]);

  if (rows.length === 0) {
    return <p className="text-xs text-slate-500">No connectors yet.</p>;
  }
  return (
    <table className="w-full text-left text-xs" data-testid="resolution-table">
      <thead className="text-slate-500">
        <tr>
          <th className="px-2 py-1">connector</th>
          <th className="px-2 py-1">winning profile</th>
          <th className="px-2 py-1">period</th>
          <th className="px-2 py-1">limit</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ connectorId, resolved }) => (
          <tr
            key={Number(connectorId)}
            className="border-t border-slate-800"
            data-testid={`resolution-row-${Number(connectorId)}`}
          >
            <td className="px-2 py-1 font-mono text-slate-100">{Number(connectorId)}</td>
            <td className="px-2 py-1 text-slate-300">
              {resolved ? `#${Number(resolved.profile.id)} ${resolved.profile.purpose}` : '—'}
            </td>
            <td className="px-2 py-1 text-slate-400">
              {resolved ? `startPeriod=${resolved.period.startPeriod}s` : '—'}
            </td>
            <td className="px-2 py-1 font-medium text-primary-soft">
              {resolved ? `${(resolved.limitW / 1000).toFixed(1)} kW` : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
